import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import api from '../../utils/api';
import { useCartCtx } from '../../App';
import { useCurrency } from '../../hooks/useCurrency';

import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import CrossfadeHero from '../../components/storefront/CrossfadeHero';
import ScrollProgress from '../../components/storefront/ScrollProgress';
import BenefitCards from '../../components/storefront/BenefitCards';
import { Icon } from '../../components/ui/Icon';

import styles from './VerdeProduct.module.css';

const ACCENT = '#2f8f4e';

// Ported from verde.html's `benefits` array. Mapped to BenefitCards' shape
// ({ icon, title, summary, more }).
const BENEFITS = [
  {
    icon: 'spa',
    title: 'Invigorates the Scalp',
    summary: 'Tap to feel it →',
    more: 'Rosemary and peppermint create a cooling, tingling rush that wakes up the scalp and makes daily root care feel like a treat.',
  },
  {
    icon: 'leaf',
    title: 'Fuller-Looking Hair',
    summary: 'Tap for more →',
    more: 'By caring for the scalp environment where hair grows, the rosemary-mint duo helps hair look and feel thicker, healthier and more alive.',
  },
  {
    icon: 'snowflake',
    title: 'Cooling Oil Balance',
    summary: 'Tap to learn →',
    more: 'Peppermint helps refresh and rebalance an oily, congested scalp, so roots stay cleaner and lighter between washes.',
  },
  {
    icon: 'leaf',
    title: 'Soothes & Refreshes',
    summary: 'Tap for details →',
    more: 'A calming herbal finish helps soothe a tight, itchy or stressed scalp, leaving it comfortable and clean-smelling.',
  },
  {
    icon: 'muscle',
    title: 'Stronger Strands',
    summary: 'Tap to expand →',
    more: 'Conditioning botanicals help hair feel more resilient and smooth, reducing the look of breakage and flyaways.',
  },
  {
    icon: 'sprout',
    title: 'Clean & Vegan',
    summary: 'Tap for the list →',
    more: 'Naturally derived, vegan and cruelty-free — formulated without sulfates, silicones or parabens for everyday use.',
  },
];

const RITUAL = [
  { n: 1, h: 'Massage', p: 'Work a few drops into a damp or dry scalp, massaging in slow circles to spread the cool tingle.' },
  { n: 2, h: 'Breathe', p: 'Let the rosemary-mint aroma sink in for a couple of minutes — a tiny daily moment of green.' },
  { n: 3, h: 'Go', p: 'Rinse or leave in. Use daily to keep roots invigorated and hair feeling fuller and fresher.' },
];

const STATS = [
  { num: '2-in-1', lbl: 'scalp therapy + hair refresher' },
  { num: '0%', lbl: 'sulfates, silicones & parabens' },
  { num: '100%', lbl: 'vegan & cruelty-free' },
  { num: <Icon name="leaf" className="w-8 h-8" />, lbl: 'naturally derived rosemary & mint' },
];

// Decorative drifting leaves (ported from the inline leaf-generator script).
const GLYPHS = ['leaf', 'sprout'];
const LEAVES = Array.from({ length: 14 }, (_, i) => ({
  key: i,
  glyph: GLYPHS[Math.floor(Math.random() * GLYPHS.length)],
  left: Math.random() * 100,
  size: 14 + Math.random() * 18,
  duration: 9 + Math.random() * 10,
  delay: -Math.random() * 16,
}));

export default function VerdeProduct() {
  const { addItem } = useCartCtx();
  const { fmt } = useCurrency();

  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    let alive = true;
    api.get('/products/verde').then((r) => {
      if (!alive) return;
      setProduct(r.data);
      setVariant(r.data.variants[0]);
    });
    return () => { alive = false; };
  }, []);

  const onAdd = () => {
    if (!variant || !product) return;
    addItem(variant, product, qty);
    toast.success('Added to bag');
  };

  if (!product || !variant) {
    return (
      <div className={styles.page}>
        <Navbar />
        <div className={styles.loading}>Loading…</div>
        <Footer />
      </div>
    );
  }

  const showCompare = variant.comparePriceInCents > variant.priceInCents;

  return (
    <div className={styles.page}>
      <ScrollProgress color="linear-gradient(90deg,#5fd6a0,#2f6b3e)" height={4} />

      {/* swaying bamboo on the edges */}
      <svg
        className={`${styles.bambooEdge} ${styles.bambooLeft}`}
        viewBox="0 0 74 800"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="verde-bgl" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#3f7a47" />
            <stop offset="1" stopColor="#6fae62" />
          </linearGradient>
        </defs>
        <rect x="14" y="0" width="22" height="800" fill="url(#verde-bgl)" />
        <g stroke="#2c5a34" strokeWidth="3">
          <line x1="14" y1="120" x2="36" y2="120" />
          <line x1="14" y1="300" x2="36" y2="300" />
          <line x1="14" y1="480" x2="36" y2="480" />
          <line x1="14" y1="660" x2="36" y2="660" />
        </g>
        <g fill="#7cbf6f">
          <path d="M36 150 q40 -18 60 6 q-40 18 -60 -6" />
          <path d="M36 500 q40 -18 60 6 q-40 18 -60 -6" />
        </g>
      </svg>
      <svg
        className={`${styles.bambooEdge} ${styles.bambooRight}`}
        viewBox="0 0 74 800"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <rect x="38" y="0" width="22" height="800" fill="url(#verde-bgl)" />
        <g stroke="#2c5a34" strokeWidth="3">
          <line x1="38" y1="200" x2="60" y2="200" />
          <line x1="38" y1="380" x2="60" y2="380" />
          <line x1="38" y1="560" x2="60" y2="560" />
          <line x1="38" y1="720" x2="60" y2="720" />
        </g>
        <g fill="#7cbf6f">
          <path d="M38 240 q-40 -18 -60 6 q40 18 60 -6" />
          <path d="M38 600 q-40 -18 -60 6 q40 18 60 -6" />
        </g>
      </svg>

      <Navbar />

      {/* ============ HERO ============ */}
      <CrossfadeHero src="/videos/verde-loop.mp4" overlay="rgba(10,40,20,.18)" className={styles.hero}>
        <div className={styles.heroVeil} />
        <div className={styles.leaves} aria-hidden="true">
          {LEAVES.map((l) => (
            <div
              key={l.key}
              className={styles.leaf}
              style={{
                left: `${l.left}vw`,
                animationDuration: `${l.duration}s`,
                animationDelay: `${l.delay}s`,
              }}
            >
              <Icon name={l.glyph} className="block" style={{ width: `${l.size}px`, height: `${l.size}px` }} />
            </div>
          ))}
        </div>
        <div className={styles.heroInner}>
          <span className={styles.kicker}>
            <Icon name="leaf" className="w-4 h-4" /> Rosemary Mint Scalp Therapy
          </span>
          <h1>
            Wake up your<br /><b>roots.</b> <em>Naturally.</em>
          </h1>
          <p>
            A cooling rosemary &amp; peppermint treatment that invigorates the scalp, fuels
            fuller-looking hair, and leaves a clean herbal tingle from root to tip.
          </p>
          <div className={styles.heroCta}>
            <a className={styles.btnPrimary} href="#buy">Shop {fmt(variant.priceInCents)}</a>
            <a className={styles.btnGhost} href="#benefits">The benefits</a>
          </div>
        </div>
        <div className={styles.scrollHint}>Scroll ↓</div>
      </CrossfadeHero>

      {/* ============ INTRO ============ */}
      <section className={styles.intro}>
        <div className={styles.wrap}>
          <div className={styles.sprig}><Icon name="sprout" className="w-8 h-8" /></div>
          <div className={styles.secHead}>
            <div className={styles.eyebrow}>Grown, not synthetic</div>
            <h2>Two herbs. <b>One tingling reset.</b></h2>
            <p>
              {product.description ||
                'VERDÉ blends garden rosemary and cooling peppermint into a lightweight scalp-and-hair therapy — bottled in bamboo-clean simplicity, free of sulfates, silicones, and parabens.'}
            </p>
          </div>
        </div>
      </section>

      {/* ============ DUO ============ */}
      <section className={styles.duo} id="duo">
        <div className={styles.wrap}>
          <div className={styles.duoGrid}>
            <div className={`${styles.duoCard} ${styles.rosemary}`}>
              <div className={styles.big}><Icon name="leaf" className="w-10 h-10" /></div>
              <h3>Rosemary</h3>
              <p>A circulation-loving herb traditionally used to invigorate the scalp and support the look of thicker, fuller, healthier hair.</p>
            </div>
            <div className={`${styles.duoCard} ${styles.mintCard}`}>
              <div className={styles.big}><Icon name="leaf" className="w-10 h-10" /></div>
              <h3>Peppermint</h3>
              <p>That signature cool tingle isn&apos;t just a feeling — peppermint refreshes the scalp, helps balance oil, and leaves hair clean and awake.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section className={styles.benefits} id="benefits">
        <div className={styles.wrap}>
          <div className={styles.secHead}>
            <div className={styles.eyebrow}>Why your scalp loves it</div>
            <h2>Benefits in <b>every drop</b></h2>
            <p>Tap a leaf to unfold the details.</p>
          </div>
          <BenefitCards items={BENEFITS} accent={ACCENT} />
        </div>
      </section>

      {/* ============ RITUAL ============ */}
      <section className={styles.ritual} id="ritual">
        <div className={styles.wrap}>
          <div className={styles.secHead}>
            <div className={styles.eyebrow}>The ritual</div>
            <h2>Three steps to a <b>fresh scalp</b></h2>
          </div>
          <div className={styles.steps}>
            {RITUAL.map((r) => (
              <div className={styles.rstep} key={r.n}>
                <div className={styles.rn}>{r.n}</div>
                <h4>{r.h}</h4>
                <p>{r.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className={styles.stats}>
        <div className={styles.wrap}>
          <div className={styles.statgrid}>
            {STATS.map((s) => (
              <div className={styles.stat} key={s.num + s.lbl}>
                <div className={styles.num}>{s.num}</div>
                <div className={styles.lbl}>{s.lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BUY ============ */}
      <section className={styles.buy} id="buy">
        <div className={styles.wrap}>
          <div className={styles.buyCard}>
            <div className={styles.eyebrow}>Bestseller</div>
            <h3>{product.name}</h3>
            <div className={styles.sub}>120ml · daily cooling scalp treatment</div>
            <div className={styles.price}>
              {fmt(variant.priceInCents)}
              {showCompare && <small>{fmt(variant.comparePriceInCents)}</small>}
            </div>
            <div className={styles.qtyrow}>
              <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity">−</button>
              <span>{qty}</span>
              <button type="button" onClick={() => setQty((q) => q + 1)} aria-label="Increase quantity">+</button>
            </div>
            <button type="button" className={`${styles.btnPrimary} ${styles.buyAdd}`} onClick={onAdd}>
              Add to Bag
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
